import {
  createFileRoute,
  Link,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight, Zap } from "lucide-react";
import { useProjects, useCreateProject } from "../lib/projects";
import { moderationErrorToast } from "../lib/moderation";
import { ContentRulesNote } from "../components/ContentRulesNote";
import { ApiError } from "../lib/api";
import { ProjectCard } from "../components/ProjectCard";
import { UnlockDownloads } from "../components/PreviewNotice";
import { PromptComposer } from "../components/PromptComposer";
import { StylePicker } from "../components/StylePicker";
import { useLastStyle, useRememberStyle } from "../lib/usePreferences";
import type { StyleId } from "../lib/styles";
import { Button, Sticker } from "../components/ui";
import { AppFooter } from "../components/AppFooter";
import { useSubscription } from "../lib/useSubscription";
import { actionAllowance, freePausedMessage } from "../lib/entitlement";
import { track } from "../lib/analytics";

export const Route = createFileRoute("/")({
  component: HomePage,
});

interface IndexLocationState {
  landingPrompt?: string;
}

const IDEAS = [
  "a sleepy owl with thick round glasses",
  "a confident robot with a single green eye",
  "a friendly coffee cup with a small smile",
  "a grumpy chili pepper with tiny arms",
  "a curious octopus holding a coffee",
  "a cute ghost with rosy cheeks",
];

function HomePage() {
  const location = useLocation();
  const initialPrompt =
    (location.state as IndexLocationState | undefined)?.landingPrompt ?? "";
  const [prompt, setPrompt] = useState(initialPrompt);
  // The picker starts on the style of the user's last new mascot.
  const lastStyle = useLastStyle();
  const [pickedStyle, setPickedStyle] = useState<StyleId | null>(null);
  const style = pickedStyle ?? lastStyle;
  const rememberStyle = useRememberStyle();
  const createProject = useCreateProject();
  const navigate = useNavigate();
  const { data: projects, isLoading } = useProjects();
  const subscription = useSubscription();

  async function onSubmit() {
    const trimmed = prompt.trim();
    if (!trimmed || createProject.isPending) return;
    try {
      const res = await createProject.mutateAsync({ prompt: trimmed, style });
      rememberStyle(style);
      track("project_create");
      setPrompt("");
      void navigate({
        to: "/projects/$id",
        params: { id: res.project_id },
      });
    } catch (err) {
      // Billing limits and moderation outcomes are already shown as a toast
      // by the mutation; the prompt stays in the input for editing.
      if (err instanceof ApiError && err.status === 402) return;
      if (moderationErrorToast(err)) return;
      throw err;
    }
  }

  const projectList = projects ?? [];
  const allowance = actionAllowance(
    subscription.data,
    "generate_character_draft"
  );
  const outOfAllowance = !!subscription.data && !allowance.affordable;

  return (
    <div className="flex flex-1 flex-col">
      {/* CREATE */}
      <section className="dot-grid border-b-2 border-line">
        <div className="mx-auto w-full max-w-2xl px-4 pb-10 pt-8 sm:pb-12 sm:pt-20">
          <div className="text-center">
            <h1 className="text-4xl leading-[1.05] text-ink sm:text-5xl">
              What's your{" "}
              <span className="relative whitespace-nowrap text-lilac-600">
                mascot
                <svg
                  aria-hidden="true"
                  viewBox="0 0 200 12"
                  className="absolute -bottom-2 left-0 h-3 w-full text-lilac-300"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M2 8 Q 50 2, 100 7 T 198 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="5"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              ?
            </h1>
            <p className="mx-auto mt-3 max-w-md text-base font-semibold text-ink-soft sm:mt-4">
              Describe a character in a sentence. The first draft takes a few
              minutes.
            </p>
          </div>

          <PromptComposer
            className="mt-6 sm:mt-8"
            size="lg"
            value={prompt}
            onChange={setPrompt}
            onSubmit={() => void onSubmit()}
            placeholder="Describe a character or a scenario — e.g. a fox finance expert with glasses and a plaid shirt…"
            ideas={IDEAS}
            autoFocus
            submitting={createProject.isPending}
            disabled={outOfAllowance}
            allowance={subscription.data ? allowance : undefined}
            accessory={
              <StylePicker
                value={style}
                onChange={setPickedStyle}
                disabled={createProject.isPending}
              />
            }
            submitLabel={
              <>
                Draw it <ArrowRight />
              </>
            }
          />
          <ContentRulesNote className="mt-3 text-center" />

          {outOfAllowance && (
            <OutOfAllowance
              paused={allowance.paused}
              resetsAt={allowance.resetsAt}
            />
          )}

          <HowItWorks />
        </div>
      </section>

      {/* GALLERY */}
      <section className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        <div className="mb-4 flex items-center gap-3 sm:mb-6">
          <h2 className="text-2xl text-ink">All mascots</h2>
          {projectList.length > 0 && (
            <Sticker tone="lilac" size="sm">
              {projectList.length}
            </Sticker>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 gap-3 sm:gap-6 md:grid-cols-3 xl:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="tactile overflow-hidden">
                <div className="aspect-square animate-pulse bg-paper-deep" />
                <div className="space-y-2 p-3">
                  <div className="h-4 w-3/4 animate-pulse rounded-full bg-paper-deep" />
                </div>
              </div>
            ))}
          </div>
        ) : projectList.length === 0 ? (
          <EmptyMascots />
        ) : (
          <>
            {subscription.data?.status === "free" && (
              <UnlockDownloads subject="your mascots" className="mb-6" />
            )}
            <div className="grid grid-cols-2 gap-3 sm:gap-6 md:grid-cols-3 xl:grid-cols-4">
              {projectList.map((p, i) => (
                <ProjectCard
                  key={p.id}
                  index={i}
                  projectId={p.id}
                  name={p.name}
                  baseJobId={p.base_job_id}
                  previewJobId={p.preview_job_id ?? p.base_job_id}
                  revisionsCount={p.revisions_count}
                  variationsCount={p.variations_count}
                  updatedAt={p.updated_at}
                />
              ))}
            </div>
          </>
        )}
      </section>
      <AppFooter />
    </div>
  );
}

const STEPS = [
  {
    tone: "face-lilac-200 edge-lilac-500",
    title: "Describe it",
    body: "One sentence is enough. We draw a first version.",
  },
  {
    tone: "face-sun edge-sun-edge",
    title: "Pick the one",
    body: "Tweak the prompt for new versions, then lock in your favourite.",
  },
  {
    tone: "face-mint edge-mint-edge",
    title: "Build the pack",
    body: "Poses and expressions of that exact character, as SVG, PNG and WebP.",
  },
];

function HowItWorks() {
  return (
    <ol className="mt-8 grid gap-3 sm:mt-10 sm:grid-cols-3">
      {STEPS.map((step, i) => (
        <li key={step.title} className="flex gap-3 sm:flex-col sm:gap-2">
          <span
            className={`flex size-8 shrink-0 items-center justify-center raised depth-3 rounded-full font-display text-sm font-bold text-ink ${step.tone}`}
          >
            {i + 1}
          </span>
          <div>
            <p className="font-display text-base font-semibold text-ink">
              {step.title}
            </p>
            <p className="text-sm font-semibold text-ink-soft">{step.body}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function OutOfAllowance({
  paused,
  resetsAt,
}: {
  /** Today's free ceiling is spent, but the user's own drafts are not. */
  paused?: boolean;
  resetsAt?: string;
}) {
  if (paused) {
    return (
      <div className="mt-4 flex flex-col gap-3 rounded-2xl border-2 border-sun-edge bg-sun/50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm font-bold text-ink">
          {freePausedMessage(resetsAt)}
        </p>
        <Button
          variant="dark"
          size="sm"
          className="shrink-0"
          render={<Link to="/settings/billing" />}
        >
          <Zap className="fill-current" /> Draw now with a plan
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-4 flex flex-col gap-3 rounded-2xl border-2 border-rose-edge bg-rose/50 p-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm font-bold text-ink">
        You've used your free drafts. Add credits to keep drawing.
      </p>
      <Button variant="dark" size="sm" render={<Link to="/settings/billing" />}>
        <Zap className="fill-current" /> Get credits
      </Button>
    </div>
  );
}

function EmptyMascots() {
  return (
    <div className="flex flex-col items-center rounded-3xl border-2 border-dashed border-line-strong px-6 py-14 text-center">
      <img
        src="/mascots/fox.webp"
        alt=""
        aria-hidden="true"
        className="h-36 w-56 animate-bob object-cover [--tilt:-4deg]"
      />
      <h3 className="mt-5 text-xl text-ink">No mascots yet</h3>
      <p className="mx-auto mt-1 max-w-sm text-sm font-semibold text-ink-soft">
        Your first one is a sentence away. Describe a character above, or press
        Tab to take the idea on show.
      </p>
    </div>
  );
}
